import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector, shallowEqual } from 'store'
import styled, { css } from 'styled-components'

import listButton from 'config/sidebar'
import { rolesWhichCanAccessAllPage } from 'constant/roles'
import useOutsideClick from 'customHooks/useOutsideClick'
import { setSidebarOpen } from 'redux/app'
import { Box } from '@astro-ui/components'
import { ArrowLeft, ArrowRight } from '@astronautsid/wpe-icons'
import theme from '@astro-ui/theme'
import useDeviceDetect from 'customHooks/useDeviceDetect'
import ButtonSideBar, {
  SidebarButtonIcon,
  SidebarButtonLabel,
  SidebarLabelWrapper,
} from './ButtonSideBar'
import { NAVBAR_HEIGHT } from '../NavBar'
import SearchBar, { type SearchBarRefType } from './SearchBar'
import SidebarEmptyState from './SidebarEmptyState'

const MIN_SEARCH_CHARS = 3

const Sidebar = () => {
  const sidebarRef = useRef()
  const searchBarRef = useRef<SearchBarRefType>(null)

  // ── Auth / layout state ──────────────────────────────────────────────────
  const {
    authorities,
    location_type,
    user_locations = [],
    pages,
  } = useAppSelector(({ auth }) => auth.userData, shallowEqual)
  const userLocationType = user_locations.map((item) => item.location_type) || []

  const isSidebarOpen = useAppSelector(({ app: { layout } }) => layout.isSidebarOpen, shallowEqual)
  const currentUserAuth = useMemo(
    () => (authorities ? authorities?.map((items) => items.name) : []),
    [authorities],
  )
  const isUserCanAccessAllPage = useMemo(
    () => rolesWhichCanAccessAllPage.some((each) => currentUserAuth.includes(each)),
    [currentUserAuth],
  )

  const dispatchRedux = useAppDispatch()
  const { isMobile } = useDeviceDetect()

  // ── Search state ─────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('')

  // ── Sidebar toggle ────────────────────────────────────────────────────────
  /** called once */
  const calledOnceOnMounted = useRef(false)
  useEffect(() => {
    if (isMobile && !calledOnceOnMounted.current) {
      dispatchRedux(setSidebarOpen(false))
      calledOnceOnMounted.current = true
    }
  }, [dispatchRedux, isMobile])

  const handleToggleSidebar = useCallback(
    (isOpen: boolean) => {
      dispatchRedux(setSidebarOpen(isOpen))
    },
    [dispatchRedux],
  )

  // When sidebar collapses mid-search, auto-clear the search field
  useEffect(() => {
    if (!isSidebarOpen) {
      setSearchQuery('')
    }
  }, [isSidebarOpen])

  useOutsideClick(sidebarRef, () => {
    if (isMobile && isSidebarOpen) {
      dispatchRedux(setSidebarOpen(false))
    }
  })

  // ── Filter logic ──────────────────────────────────────────────────────────
  const isSearching = searchQuery.length >= MIN_SEARCH_CHARS

  const filteredListButton = useMemo(() => {
    if (!isSearching) return listButton

    const keyword = searchQuery.toLowerCase()

    return listButton.reduce((acc, item) => {
      const l1Match = item.name.toLowerCase().includes(keyword)

      if (item.subMenu?.length) {
        if (l1Match) {
          // L1 matches → show L1 with ALL its L2 children
          return [...acc, item]
        }

        // L1 doesn't match → keep only L2s whose labels match
        const matchingChildren = item.subMenu.filter((sub) =>
          sub.name.toLowerCase().includes(keyword),
        )

        if (matchingChildren.length > 0) {
          return [...acc, { ...item, subMenu: matchingChildren }]
        }

        return acc
      }

      // Leaf L1 (no children) — show only if its name matches
      if (l1Match) return [...acc, item]

      return acc
    }, [] as typeof listButton)
  }, [isSearching, searchQuery])

  const hasNoResults = isSearching && filteredListButton.length === 0

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Container ref={sidebarRef as never} isMobile={isMobile} isSidebarOpen={isSidebarOpen}>
        {!isMobile && (
          <Box
            borderBottom={`1px solid ${theme.palette.divider}`}
            position="sticky"
            top={0}
            backgroundColor={theme.palette.background.paper}
            zIndex={theme.zIndex.drawer}
          >
            <ButtonToggleSidebar onClick={() => handleToggleSidebar(!isSidebarOpen)}>
              <SidebarButtonIcon>
                {isSidebarOpen ? <ArrowLeft /> : <ArrowRight />}
              </SidebarButtonIcon>
              <SidebarLabelWrapper isSidebarCollapsed={!isSidebarOpen}>
                <SidebarButtonLabel>Sembunyikan Menu</SidebarButtonLabel>
              </SidebarLabelWrapper>
            </ButtonToggleSidebar>

            {/* ── Search Bar ───────────────────────────────────────────────── */}
            <Box paddingBottom="8px">
              <SearchBar
                ref={searchBarRef}
                value={searchQuery}
                onChange={setSearchQuery}
                isSidebarCollapsed={!isSidebarOpen}
                onExpandSidebar={() => handleToggleSidebar(true)}
              />
            </Box>
          </Box>
        )}

        {/* ── Menu list or empty state ───────────────────────────────────── */}
        {hasNoResults ? (
          <SidebarEmptyState />
        ) : (
          filteredListButton.map((el) => (
            <ButtonSideBar
              key={el.name}
              middleware={!isUserCanAccessAllPage}
              currentUserAuth={currentUserAuth}
              location_type={location_type}
              userLocationType={userLocationType}
              data={el}
              allowedPageID={pages}
              isSidebarCollapsed={!isSidebarOpen}
              onOpenSidebar={handleToggleSidebar}
              // Force-expand any L1 that has children while a search is active
              isForceExpand={isSearching && el.subMenu && el.subMenu.length > 0 ? true : undefined}
            />
          ))
        )}
      </Container>
    </>
  )
}

export default Sidebar

const Container = styled.aside<{ isMobile: boolean; isSidebarOpen: boolean }>`
  padding-bottom: 20px;
  background-color: ${({ theme: { palette } }) => palette.background.paper};
  border-right: 1px solid #cad3e1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: all ease 0.3s;
  z-index: ${({ theme: { zIndex } }) => zIndex.drawer};
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none; /* IE and Edge */
  /* Chrome, Edge, dan Safari */
  &::-webkit-scrollbar {
    display: none;
  }

  ${({ isMobile }) =>
    !isMobile &&
    `
    height: calc((var(--vh, 1vh) * 100) - ${NAVBAR_HEIGHT}px);
  `}

  ${({ isMobile }) =>
    isMobile &&
    `
    position: fixed;
    top: ${NAVBAR_HEIGHT}px;
    bottom: 0;
  `}

  ${({ isMobile, isSidebarOpen }) =>
    isMobile &&
    css`
      transform: translateX(${isSidebarOpen ? '0%' : '-100%'});
      $
    `}
`

const ButtonToggleSidebar = styled.button`
  display: flex;
  padding: 10px 0;
  text-align: left;
  background: transparent;
  border: none;
`
